import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Control, UseFormSetValue, useFormState, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import AddIcon from '@mui/icons-material/Add'
import { Autocomplete, Box, TextField } from '@mui/material'

import { searchCustomers } from '../../../service/customers.service'
import { CustomerDto } from '../../../service/types'
import { CustomerDialog } from '../../../views/Customers/CustomerDialog'

const CREATE_OPTION_ID = '__create__'

const classifyInput = (text: string): { name?: string; phone?: string; email?: string } => {
  const trimmed = text.trim()
  if (trimmed.includes('@')) {
    return { email: trimmed }
  }
  if (/^[+\d][\d\s\-()]+$/.test(trimmed)) {
    return { phone: trimmed }
  }
  return { name: trimmed }
}

type Props = {
  control: Control<any>
  setValue: UseFormSetValue<any>
  label?: string
  customerIdField?: string
  clientNameField?: string
  disabled?: boolean
}

export const CustomerAutocomplete: FC<Props> = ({
  control,
  setValue,
  label: _label = 'Client',
  customerIdField = 'customerId',
  clientNameField = 'clientName',
  disabled,
}) => {
  const { t } = useTranslation()
  const [options, setOptions] = useState<CustomerDto[]>([])
  const [inputValue, setInputValue] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const pendingDefaults = useRef<{ name?: string; phone?: string; email?: string }>({})

  const currentCustomerId = useWatch({ control, name: customerIdField })
  const currentClientName = useWatch({ control, name: clientNameField })
  const { errors } = useFormState({ control, name: customerIdField })
  const customerError = errors[customerIdField]

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  // Sync input display with form state (e.g. when form resets or editing existing data)
  useEffect(() => {
    if (currentClientName) {
      setInputValue(currentClientName)
    } else {
      setInputValue('')
    }
  }, [currentClientName])

  // Resolve customerId to customer name when we have an ID but no name
  // (e.g. when "Convert to Lesson" passes only a customerId via query params)
  useEffect(() => {
    if (currentCustomerId && !currentClientName) {
      let cancelled = false
      void (async () => {
        try {
          const { getCustomer } = await import('../../../service/customers.service')
          const response = await getCustomer(currentCustomerId)
          if (!cancelled) {
            setValue(clientNameField, response.data.name)
            setInputValue(response.data.name)
          }
        } catch {
          // Silently ignore — user can type manually
        }
      })()
      return () => {
        cancelled = true
      }
    }
  }, [currentCustomerId, currentClientName, setValue, clientNameField])

  const doSearch = useCallback(async (query: string) => {
    if (query.length < 1) {
      setOptions([])
      return
    }
    try {
      const response = await searchCustomers(query)
      setOptions(response.data)
    } catch {
      setOptions([])
    }
  }, [])

  const handleInputChange = (_event: React.SyntheticEvent, value: string, reason: string) => {
    setInputValue(value)

    if (reason === 'input') {
      // User is typing — clear customerId, set clientName
      setValue(customerIdField, '')
      setValue(clientNameField, value)

      // Debounced search
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => doSearch(value), 300)
    }
  }

  const handleChange = (_event: React.SyntheticEvent, value: CustomerDto | string | null) => {
    if (value && typeof value === 'object') {
      if (value.id === CREATE_OPTION_ID) {
        // User clicked "Create new customer" — route typed text to correct field
        pendingDefaults.current = classifyInput(value.name)
        setCreateDialogOpen(true)
        return
      }
      // Selected a customer from the list
      setValue(customerIdField, value.id)
      setValue(clientNameField, value.name)
      setInputValue(value.name)
    } else if (typeof value === 'string') {
      // Free-typed value
      setValue(customerIdField, '')
      setValue(clientNameField, value)
    } else {
      // Cleared
      setValue(customerIdField, '')
      setValue(clientNameField, '')
      setInputValue('')
    }
  }

  const handleCustomerCreated = (customer: CustomerDto) => {
    setValue(customerIdField, customer.id)
    setValue(clientNameField, customer.name)
    setInputValue(customer.name)
  }

  const getOptionLabel = (option: CustomerDto | string) => {
    if (typeof option === 'string') return option
    return option.name
  }

  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: CustomerDto) => {
    if (option.id === CREATE_OPTION_ID) {
      return (
        <li {...props} key={CREATE_OPTION_ID}>
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
            <AddIcon fontSize="small" sx={{ mr: 0.5 }} />
            <span>{t('customers.createOption', { name: option.name })}</span>
          </Box>
        </li>
      )
    }

    const details = [option.phone, option.email].filter(Boolean).join(' / ')
    return (
      <li {...props} key={option.id}>
        <span>
          <strong>{option.name}</strong>
          {details && <span style={{ marginLeft: 8, opacity: 0.6, fontSize: '0.85em' }}>{details}</span>}
        </span>
      </li>
    )
  }

  // Find the selected customer object for controlled value
  const selectedCustomer = currentCustomerId ? (options.find((o) => o.id === currentCustomerId) ?? null) : null

  return (
    <>
      <Autocomplete
        freeSolo
        disabled={disabled}
        options={options}
        value={selectedCustomer}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleChange}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        filterOptions={(opts) => {
          if (inputValue.trim()) {
            return [...opts, { id: CREATE_OPTION_ID, name: inputValue.trim() } as CustomerDto]
          }
          return opts
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            error={!!customerError}
            helperText={customerError?.message as string | undefined}
          />
        )}
      />

      <CustomerDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSaved={() => {}}
        defaults={pendingDefaults.current}
        onCreated={handleCustomerCreated}
      />
    </>
  )
}
